<script setup lang="ts">
import { computed, ref } from 'vue';
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';
import {
  Bars3Icon,
  BanknotesIcon,
  CircleStackIcon,
  ShieldCheckIcon,
  WalletIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import ImmersveLogo from '@/assets/ImmersveLogo.vue';
import AccountMenu from '@/components/ui/AccountMenu.vue';
import {
  AccountActions,
  AmlReviewActions,
  CardActions,
  CardTokenActions,
  FundingSourceActions,
} from '@/integrations/Immersve/auth/actions';
import { useAuthStore } from '@/stores/auth.store';
import { useSettingsStore } from '@/stores/settings.store';

const authStore = useAuthStore();
const settingsStore = useSettingsStore();

// An app is listed only for users who hold at least one of its actions on every
// account (`account:*`), the same check its page makes before rendering.
const apps = [
  {
    name: 'AML Review',
    href: '/aml-review',
    icon: ShieldCheckIcon,
    actions: [AmlReviewActions.Read],
  },
  {
    name: 'Cards',
    href: '/cards',
    icon: WalletIcon,
    actions: [AccountActions.Read, CardActions.Read, CardTokenActions.Read],
  },
  {
    name: 'Funding sources',
    href: '/funding-sources',
    icon: CircleStackIcon,
    actions: [AccountActions.Read, FundingSourceActions.Read],
  },
  {
    name: 'Payment details',
    href: '/payments',
    icon: BanknotesIcon,
    actions: [AccountActions.Read],
  },
];
const appsNavigation = computed(() =>
  apps.filter((app) => app.actions.some((action) => authStore.user?.isAuthorised(action) ?? false))
);

const sidebarOpen = ref(false);
</script>

<template>
  <div>
    <div class="flex h-screen flex-col xl:pl-72">
      <main class="flex-1 overflow-auto bg-white pt-16 xl:pt-0" data-scroll-container>
        <slot />
      </main>

      <!-- MOBILE NAV OPEN BUTTON -->
      <button
        type="button"
        class="fixed top-4 left-4 z-40 p-2.5 text-black xl:hidden"
        @click="sidebarOpen = true"
      >
        <span class="sr-only">Open sidebar</span>
        <Bars3Icon class="size-6" aria-hidden="true" />
      </button>

      <!-- ACCOUNT MENU -->
      <AccountMenu
        :theme="settingsStore.preference"
        :local-dates="settingsStore.localDates"
        @toggle-theme="settingsStore.toggleTheme()"
        @update:local-dates="settingsStore.setLocalDates"
      />
    </div>

    <!-- DESKTOP NAV -->
    <div class="hidden xl:fixed xl:inset-y-0 xl:z-50 xl:flex xl:w-72 xl:flex-col">
      <div
        class="ring-white5 flex w-full grow flex-col gap-y-5 overflow-y-auto bg-white px-6 ring-1"
      >
        <a
          href="/"
          class="group -mx-2 flex h-16 rounded-md p-2 pt-2 text-sm/6 font-semibold text-black hover:text-indigo-400"
        >
          <ImmersveLogo class="shrink-0 p-2" />
          <div class="mt-auto mb-[1px] px-1 text-xl font-semibold">Admin</div>
        </a>
        <nav class="flex flex-1 flex-col">
          <ul role="list" class="flex flex-1 flex-col gap-y-7">
            <li>
              <div class="px-2 text-xs font-semibold tracking-wide text-gray-400 uppercase">
                Apps
              </div>
              <ul role="list" class="-mx-2 mt-2 space-y-1">
                <li v-for="item in appsNavigation" :key="item.name">
                  <RouterLink
                    :active-class="'bg-gray-100 text-black'"
                    :to="item.href"
                    :class="'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-700 hover:bg-gray-100 hover:text-black'"
                  >
                    <component :is="item.icon" class="size-6 shrink-0" aria-hidden="true" />
                    {{ item.name }}
                  </RouterLink>
                </li>
              </ul>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- MOBILE NAV -->
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog class="relative z-50 xl:hidden" @close="sidebarOpen = false">
        <TransitionChild
          as="template"
          enter="transition-opacity ease-linear duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-100 opacity-95" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild
            as="template"
            enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leave-from="translate-x-0"
            leave-to="-translate-x-full"
          >
            <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
              <TransitionChild
                as="template"
                enter="ease-in-out duration-300"
                enter-from="opacity-0"
                enter-to="opacity-100"
                leave="ease-in-out duration-300"
                leave-from="opacity-100"
                leave-to="opacity-0"
              >
                <div class="absolute top-0 left-full flex w-16 justify-center pt-5">
                  <button type="button" class="-m-2.5 p-2.5" @click="sidebarOpen = false">
                    <span class="sr-only">Close sidebar</span>
                    <XMarkIcon class="size-6 text-black" aria-hidden="true" />
                  </button>
                </div>
              </TransitionChild>
              <div
                class="ring-white5 flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 ring-1"
              >
                <a
                  href="/"
                  class="group -mx-2 flex h-16 rounded-md p-2 pt-2 text-sm/6 font-semibold text-black hover:text-indigo-400"
                >
                  <ImmersveLogo class="shrink-0 p-2" />
                  <div class="mt-auto mb-[1px] px-1 text-xl font-semibold">Admin</div>
                </a>
                <nav class="flex flex-1 flex-col">
                  <ul role="list" class="flex flex-1 flex-col gap-y-7">
                    <li>
                      <div class="px-2 text-xs font-semibold tracking-wide text-gray-400 uppercase">
                        Apps
                      </div>
                      <ul role="list" class="-mx-2 mt-2 space-y-1">
                        <li v-for="item in appsNavigation" :key="item.name">
                          <RouterLink
                            :active-class="'bg-gray-100 text-black'"
                            :to="item.href"
                            :class="'group flex gap-x-3 rounded-md p-2 text-sm/6 font-semibold text-gray-700 hover:bg-gray-100 hover:text-black'"
                          >
                            <component :is="item.icon" class="size-6 shrink-0" aria-hidden="true" />
                            {{ item.name }}
                          </RouterLink>
                        </li>
                      </ul>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>
